import React from "react";
import { Download, Camera } from "lucide-react";

// ---------- Top editorial trio ----------
const HERO_TRIO = [
  {
    id: "studio-01-pinstripe",
    url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/7c1fbd5bd174466d90c166cffdfbd6dd_image.bin",
    caption: "Editorial · Pinstripe",
    span: "md:col-span-7 md:row-span-2",
    height: "h-[420px] md:h-full md:min-h-[640px]",
    objectPos: "object-center",
  },
  {
    id: "studio-02-portrait",
    url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/609bac40af59483f84da0a5739d14161_image.bin",
    caption: "Studio · Portrait",
    span: "md:col-span-5 md:row-span-1",
    height: "h-[300px]",
    objectPos: "object-top",
  },
  {
    id: "studio-03-detail",
    url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/0c0de785160a4a3ebc974f99b2a4b214_image.bin",
    caption: "Studio · Detail",
    span: "md:col-span-5 md:row-span-1",
    height: "h-[300px]",
    objectPos: "object-center",
  },
];

// ---------- Black-suit editorial set ----------
const BLACK_SUIT_SET = [
  {
    id: "black-suit-profile-01",
    url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/1f5527de277941ae812d9e4e9dd0a39c_image.bin",
    caption: "Profile · Noir",
    span: "md:col-span-4",
    height: "h-[440px] md:h-[560px]",
    objectPos: "object-center",
  },
  {
    id: "black-suit-fullbody",
    url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/2e48c538e88a44bab076d7d2d61a4b40_image.bin",
    caption: "Full Body · Tailored",
    span: "md:col-span-4",
    height: "h-[440px] md:h-[560px]",
    objectPos: "object-top",
  },
  {
    id: "black-suit-profile-02",
    url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/940a81f7e591430899f9cc1ef45dab9c_image.bin",
    caption: "Editorial · Statement",
    span: "md:col-span-4",
    height: "h-[440px] md:h-[560px]",
    objectPos: "object-center",
  },
];

// ---------- Lifestyle row ----------
const LIFESTYLE_PAIR = [
  {
    id: "lifestyle-grey-jacket",
    url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/9b79b9709e5c4aaf962e2db3bade0496_image.bin",
    caption: "Lifestyle · Daylight",
    span: "md:col-span-5",
    height: "h-[400px] md:h-[520px]",
    objectPos: "object-center",
  },
  {
    id: "black-suit-side",
    url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/d502cb0a04e547d3a1771f3e40f157c8_image.bin",
    caption: "Editorial · Side",
    span: "md:col-span-7",
    height: "h-[400px] md:h-[520px]",
    objectPos: "object-top",
  },
];

const SOFA = {
  id: "lifestyle-sofa",
  url: "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/bbad684bd5234c66804427a8ad1b96f8_image.bin",
};

function Tile({ tile, eager = false }) {
  const downloadName = `daniel-lopes-${tile.id}.jpg`;
  return (
    <figure
      data-testid={`gallery-tile-${tile.id}`}
      className={`relative overflow-hidden zoom-wrap group border border-white/5 hover:border-[#D4AF37]/40 transition-colors duration-500 ${tile.span} ${tile.height}`}
    >
      <img
        src={tile.url}
        alt={`Daniel Lopes ${tile.caption}`}
        loading={eager ? "eager" : "lazy"}
        className={`w-full h-full object-cover ${tile.objectPos}`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

      {/* Download button — appears on hover */}
      <a
        href={tile.url}
        download={downloadName}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`gallery-download-${tile.id}`}
        aria-label={`Foto ${tile.caption} herunterladen`}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center bg-[#0A0A0A]/80 backdrop-blur-md border border-white/15 text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] hover:border-[#D4AF37] transition-all duration-300 opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        <Download size={16} strokeWidth={1.5} />
      </a>

      <figcaption className="absolute bottom-0 left-0 right-0 p-5 md:p-7 flex items-end justify-between gap-4">
        <span className="text-xs tracking-[0.24em] uppercase text-white font-body">
          {tile.caption}
        </span>
        <Camera size={14} strokeWidth={1.5} className="text-[#D4AF37] opacity-70" />
      </figcaption>
    </figure>
  );
}

function RowDivider({ label }) {
  return (
    <div className="flex items-center gap-5 my-4 md:my-6">
      <span className="gold-rule" />
      <span className="overline">{label}</span>
      <span className="flex-1 h-px bg-white/5" />
    </div>
  );
}

export default function Gallery() {
  return (
    <section
      id="galerie"
      data-testid="gallery-section"
      className="relative bg-[#0A0A0A] py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="overline" data-testid="gallery-overline">
              <span className="gold-rule mr-4 align-middle" />
              Press · Editorial · 2025
            </p>
            <h2
              className="font-display mt-4 text-white leading-tight tracking-tight"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", fontWeight: 600 }}
              data-testid="gallery-headline"
            >
              Im <span className="italic text-[#D4AF37]">Studio.</span>
              <br />
              Im Moment.
            </h2>
          </div>
          <p className="text-[#A3A3A3] max-w-md font-body text-sm leading-relaxed">
            Aktuelle Pressefotos in voller Auflösung — frei zur Nutzung in
            Programmheften, Pressemitteilungen und Werbematerialien nach
            Booking-Bestätigung. <span className="text-[#D4AF37]">Per Hover auf jedes Bild
            erscheint ein Download-Button.</span>
          </p>
        </div>

        {/* Trio Row */}
        <RowDivider label="Editorial · Studio" />
        <div
          className="grid grid-cols-1 md:grid-cols-12 md:grid-rows-2 gap-4 md:gap-6"
          data-testid="gallery-grid-trio"
        >
          {HERO_TRIO.map((t, i) => (
            <Tile key={t.id} tile={t} eager={i === 0} />
          ))}
        </div>

        {/* Black-suit set */}
        <RowDivider label="Signature · Black Series" />
        <div
          className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6"
          data-testid="gallery-grid-noir"
        >
          {BLACK_SUIT_SET.map((t) => (
            <Tile key={t.id} tile={t} />
          ))}
        </div>

        {/* Lifestyle pair */}
        <RowDivider label="Lifestyle · Off-Stage" />
        <div
          className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6"
          data-testid="gallery-grid-lifestyle"
        >
          {LIFESTYLE_PAIR.map((t) => (
            <Tile key={t.id} tile={t} />
          ))}
        </div>

        {/* Full-width hero sofa with Pressekit CTA */}
        <figure
          data-testid="gallery-tile-lifestyle-sofa"
          className="relative overflow-hidden zoom-wrap group border border-white/5 hover:border-[#D4AF37]/40 transition-colors duration-500 mt-4 md:mt-6 h-[420px] md:h-[600px]"
        >
          <img
            src={SOFA.url}
            alt="Daniel Lopes Lifestyle Sofa"
            loading="lazy"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/20 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/70 via-transparent to-transparent" />
          <figcaption className="absolute bottom-0 left-0 right-0 p-8 md:p-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-md">
              <p className="overline mb-3">Off-Stage · Lifestyle</p>
              <h3 className="font-display text-2xl md:text-4xl text-white leading-tight">
                Der Mensch hinter
                <br />
                der <span className="italic text-[#D4AF37]">Bühne.</span>
              </h3>
            </div>
            <div className="flex flex-col md:flex-row gap-3 self-start md:self-auto">
              <a
                href={SOFA.url}
                download="daniel-lopes-lifestyle-sofa.jpg"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gallery-sofa-download"
                className="inline-flex items-center gap-3 px-5 py-3 border border-[#D4AF37] text-xs tracking-[0.2em] uppercase text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
              >
                <Download size={14} strokeWidth={1.5} />
                Foto herunterladen
              </a>
              <a
                href="#booking"
                data-testid="gallery-press-cta"
                className="inline-flex items-center gap-3 px-5 py-3 bg-[#D4AF37] text-xs tracking-[0.2em] uppercase text-[#0A0A0A] hover:bg-[#F3E5AB] transition-colors"
              >
                Pressekit anfordern
              </a>
            </div>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
